/**
 * API Campaigns List Component
 *
 * Professional list view for API campaigns (campaign_type = 3) with:
 * - Infinite scroll
 * - Clean list design matching Broadcasts
 * - Search capabilities
 * - Status badges and metrics
 * - Responsive layout
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton, Tooltip, Popover, Modal } from 'antd';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';

// assets
import Delete from '../../../../assets/customSVG/Delete';
import ClearFilters from '../../../../assets/customSVG/ClearFilters';
import Calendar from '../../../../assets/customSVG/navCalendar';
import { ICONS } from '../../../../assets/icons';

// helpers
import { cn } from '../../../../helper/cn';
import { dateConverter } from '../../../../helper/dateConverter';
import {
  useAspSelector,
  useAspDispatch,
} from '../../../../test/jest-redux-hooks';

// api
import { campaignList } from '../../../../api/apiClients/campaign';
import { campaignDelete } from '../../../campaign/api/Api';

// constants
import { CAMPAIGN_STATUS } from '../../../campaigns/constants/campaigns';
import { STATUS, SORT } from '../../../campaigns/constants/campaignList';

// components
import Loader from '../../../../components/commonComponents/Loader/Index';
import NoData from '../../../../components/commonComponents/Empty/NoData';
import SearchBar from '../../../../components/commonComponents/searchBar';
import CheckBoxSqure from '../../../../components/commonComponents/checkbox/CheckBoxSqure';
import NewRadioButton from '../../../../components/form/radio/NewRadio';
import NewAlert from '../../../../components/commonComponents/modal/alert/NewAlert';
import { updateToggleToast } from '../../../../reduxToolkit/appSlice';

// feature gating
import { useFeature } from '../../../../hooks/useFeatureGating';
import { FEATURES } from '../../../../constant/featureSlugs';
import PremiumUpgradeScreen from '../../../../components/commonComponents/PremiumUpgradeScreen/PremiumUpgradeScreen';

const API_CAMPAIGN_TYPE = 3;

const ApiCampaigns = () => {
  const navigate = useNavigate();
  const dispatch = useAspDispatch();
  const { currentBrand, toggleToast } = useAspSelector((state) => state.app);
  const { isEnabled: canUseApi } = useFeature(FEATURES.API_ACCESS);
  const listContainerRef = useRef(null);

  // State management
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // Filter and Sort states
  const [status, setStatus] = useState(STATUS);
  const [sort, setSort] = useState(SORT);
  const [sortLabel, setSortLabel] = useState('Newest First');

  // Delete modal state
  const [discardAlertOpen, setDiscardAlertOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);

  // Popover state for actions
  const [startPopoverVisible, setStartPopoverVisible] = useState({});

  /**
   * Build sort data from state
   */
  const getSortData = useCallback(() => {
    let data = {};
    sort?.forEach((s) => {
      if (s?.id === 1) {
        s?.options?.forEach((o) => {
          if (o?.isSelected) data['sort_by'] = o?.status;
        });
      }
      if (s?.id === 2) {
        s?.options?.forEach((o) => {
          if (o?.isSelected) data['order_by'] = o?.status;
        });
      }
    });
    return data;
  }, [sort]);

  /**
   * Get selected status filters
   */
  const getSelectedStatus = useCallback(() => {
    const res = status?.filter((s) => s?.isSelected === true);
    const ids = res?.map((r) => r?.status);
    return ids?.length === STATUS?.length || ids?.length === 0 ? null : ids;
  }, [status]);

  /**
   * Fetch campaigns from API
   */
  const fetchCampaigns = useCallback(
    async (isLoadMore = false) => {
      if (!currentBrand?.brand_id || !canUseApi) return;

      try {
        if (!isLoadMore) {
          setLoading(true);
          setCampaigns([]);
          setPage(1);
          setLimit(20);
          setHasMore(true);
        }

        const currentLimit = isLoadMore ? limit + 20 : 20;
        const sortData = getSortData();

        const payload = {
          page: 1,
          limit: currentLimit,
          name: searchKey,
          status: getSelectedStatus(),
          campaign_type: API_CAMPAIGN_TYPE,
          ...sortData,
        };

        const response = await campaignList(currentBrand.brand_id, payload);

        if (response?.status === 200) {
          const data = response.data?.campaigns || [];
          const totalCount = response.data?.campaigns_count || 0;

          setCampaigns(data);
          setTotal(totalCount);
          setLimit(currentLimit);

          if (data.length >= totalCount) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error('Error fetching API campaigns:', error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [
      currentBrand?.brand_id,
      searchKey,
      status,
      sort,
      canUseApi,
      limit,
      getSortData,
      getSelectedStatus,
    ],
  );

  useEffect(() => {
    if (canUseApi) {
      fetchCampaigns(false);
    }
  }, [searchKey, status, sort, currentBrand?.brand_id, canUseApi]);

  const loadNextPage = () => {
    fetchCampaigns(true);
  };

  /**
   * Handle search input change
   */
  const handleSearch = (value) => {
    setSearchKey(value);
  };

  const onStatusChange = (item) => {
    setStatus((prev) =>
      prev.map((s) =>
        s.id === item.id ? { ...s, isSelected: !s.isSelected } : s,
      ),
    );
  };

  const clearStatus = () => {
    setStatus((prev) => prev.map((s) => ({ ...s, isSelected: false })));
  };

  const onSortChange = (sectionId, item) => {
    setSort((prev) =>
      prev.map((section) => ({
        ...section,
        options: section.options.map((option) => ({
          ...option,
          isSelected: option.id === item.id,
        })),
      })),
    );
    setSortLabel(item.label);
  };

  const allowAllClear = () => {
    return status.some((s) => s.isSelected);
  };

  /**
   * Handle campaign deletion
   */
  const handleDeleteClick = (e, record) => {
    e.stopPropagation();
    setCampaignToDelete(record);
    setDiscardAlertOpen(true);
    setStartPopoverVisible({});
  };

  const deleteCampaign = () => {
    if (!campaignToDelete) return;

    setDeleteLoader(true);
    campaignDelete(campaignToDelete.id, currentBrand?.brand_id).then((res) => {
      setDeleteLoader(false);
      setDiscardAlertOpen(false);

      if (res.status === 200) {
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: 'Campaign deleted successfully',
              status: 'Success',
              duration: '',
            },
          ]),
        );
        fetchCampaigns(false);
      } else {
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content:
                res?.response?.data?.error || 'Failed to delete campaign',
              status: 'Error',
              duration: '',
            },
          ]),
        );
      }
      setCampaignToDelete(null);
    });
  };

  /**
   * Handle row click to view details
   */
  const handleNavigateCampaign = (record) => {
    navigate(`/user/settings/api-campaigns/${record.id}`, {
      state: { item: record },
    });
  };

  const togglePopoverVisibility = (id) => {
    setStartPopoverVisible((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // styles
  const bodyStyle =
    'text-sm weight-medium text-[var(--font-600)] text-left pl-2';
  const dotStyle =
    'size-1.5 contents-[""] rounded-full group-hover/dots:!bg-[var(--BG-100)]';

  if (!canUseApi) {
    return (
      <div className="w-full h-full">
        <PremiumUpgradeScreen
          title="Unlock API Campaigns"
          message="Create and manage API-triggered campaigns to automate your marketing workflows. Upgrade to access this feature."
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col p-6">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-semibold text-gray-900">API Campaigns</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage your API-triggered campaigns. These campaigns are executed via
          API calls for programmatic marketing automation.
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8E8EA]">
        {/* Filter/Search Section */}
        <div className="w-full py-4 px-5 flex items-center justify-between gap-5 border-b border-[var(--border-50)]">
          <h4 className="new-header3">
            {total > 0 ? `${total} Total API Campaigns` : 'API Campaigns'}
          </h4>
          <div className="flex items-center gap-4">
            <SearchBar
              searchKey={searchKey}
              setSearchKey={setSearchKey}
              onChange={handleSearch}
              loading={false}
              expandable={true}
            />

            {/* Sort Dropdown */}
            <Popover
              trigger={['click']}
              placement="bottom"
              arrow={false}
              content={
                <div className="min-w-56 px-3.5 py-2.5">
                  {sort?.map((section) => (
                    <div key={section?.heading}>
                      <h3 className="new-header1 mb-2 pt-5">
                        {section.heading}
                      </h3>
                      {section?.options?.map((s) => (
                        <div
                          key={s.id}
                          className="w-full h-9 flex items-center gap-2.5 py-1 cursor-pointer"
                          onClick={() => onSortChange(section?.id, s)}
                        >
                          <div className="w-5 h-9 flex items-center justify-center mt-1">
                            <NewRadioButton
                              value={s?.isSelected}
                              selected={s?.isSelected}
                              onChange={() => {}}
                              rootClassName={
                                s?.isSelected && 'size-3.5 new-radio'
                              }
                            />
                          </div>
                          {s?.icon && <img src={s?.icon} alt={s.label} />}
                          <p className="font-medium text-sm text-[#202421]">
                            {s?.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              }
            >
              <div className="group/sort flex items-center gap-2.5 bg-[#F9FAFB] border border-[#E8E8EA] px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-[#616874]">
                  {sortLabel || 'Newest First'}
                </div>
                <div className="border-[4px] border-transparent border-t-[#616874] translate-y-[2px]"></div>
              </div>
            </Popover>

            {/* Status Filter */}
            <Popover
              trigger={['click']}
              arrow={false}
              placement="bottom"
              content={
                <div className="min-w-56 px-3.5 py-2.5">
                  <div className="w-full h-8 flex items-center justify-between gap-5 py-2.5 text-sm weight-medium">
                    <div className="text-[#616874]">Status</div>
                    <div
                      className={cn(
                        'text-[#F96056]',
                        status.some((s) => s.isSelected)
                          ? 'cursor-pointer opacity-100'
                          : 'cursor-not-allowed opacity-50',
                      )}
                      onClick={() => {
                        if (status.some((s) => s.isSelected)) clearStatus();
                      }}
                    >
                      Clear
                    </div>
                  </div>
                  {status?.map((s) => (
                    <div
                      className="w-full h-9 flex items-center gap-2.5 py-1 cursor-pointer"
                      onClick={() => onStatusChange(s)}
                      key={s?.id}
                    >
                      <CheckBoxSqure
                        value={s?.isSelected}
                        onChange={() => {}}
                        className="size-4"
                      />
                      {s?.icon && (
                        <img src={s?.icon} alt={s.label} className="w-4" />
                      )}
                      <p className="font-medium text-sm text-[#202421]">
                        {s?.label}
                      </p>
                    </div>
                  ))}
                </div>
              }
            >
              <div className="group/goal flex items-center gap-2.5 bg-[#F9FAFB] border border-[#E8E8EA] px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-[#616874] flex items-center">
                  Status
                  {status.some((s) => s.isSelected) && (
                    <div className="ml-1.5 rounded-full bg-[#6940F2] text-white text-[10px] w-4 h-4 flex justify-center items-center font-bold">
                      {status.filter((s) => s.isSelected).length}
                    </div>
                  )}
                </div>
                <div className="border-[4px] border-transparent border-t-[#616874] translate-y-[2px]"></div>
              </div>
            </Popover>

            {/* Clear All Filters */}
            <div
              className={cn(
                'group/status flex items-center gap-2.5 hover:bg-gray-50 px-2.5 py-2.5 rounded-lg transition-colors',
                allowAllClear()
                  ? 'cursor-pointer opacity-100'
                  : 'cursor-not-allowed opacity-50',
              )}
              onClick={() => {
                if (allowAllClear()) {
                  clearStatus();
                }
              }}
            >
              <ClearFilters
                width={16}
                height={16}
                color={allowAllClear() ? '#202421' : '#616874'}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="w-full relative h-full overflow-hidden flex-1 px-5">
          <Skeleton
            loading={loading && campaigns.length === 0}
            active
            paragraph={{ rows: 8 }}
          >
            {campaigns?.length > 0 && (
              <div className="w-full h-14 sticky top-0 flex items-center pr-1 border-b border-gray-100 bg-white z-10">
                <div className={cn('new-header2', 'w-[35%] pl-3')}>
                  Campaign Name
                </div>
                <div className={cn('new-header2', 'w-[15%] text-center')}>
                  Status
                </div>
                <div className={cn('new-header2', 'w-[15%] text-center')}>
                  Total Contacts
                </div>
                <div className={cn('new-header2', 'w-[10%] text-center')}>
                  Sent %
                </div>
                <div className={cn('new-header2', 'w-[10%] text-center')}>
                  Delivered %
                </div>
                <div className={cn('new-header2', 'w-[15%] text-center')}>
                  Open Rate %
                </div>
              </div>
            )}

            <div
              className="min-w-full h-full min-h-[50vh] overflow-hidden overflow-y-scroll"
              id="apiCampaignsScrollableDiv"
              ref={listContainerRef}
            >
              <InfiniteScroll
                dataLength={campaigns?.length || 0}
                next={loadNextPage}
                hasMore={hasMore}
                scrollableTarget="apiCampaignsScrollableDiv"
                height={loading ? '45vh' : '50vh'}
                className="!pr-1 pb-24 listScroll"
                loader={
                  <div className="w-full h-[5vh] flex items-center justify-center bg-white rounded-lg mt-2">
                    <Loader Width={30} Height={30} />
                  </div>
                }
              >
                {campaigns?.length > 0 ? (
                  <>
                    {campaigns?.map((l) => {
                      const date = dateConverter(l?.created_at);
                      const campaignStatus =
                        CAMPAIGN_STATUS[l?.status] || CAMPAIGN_STATUS[10];

                      return (
                        <div
                          key={l?.id}
                          className="w-full odd:bg-gray-50 my-1 py-4 flex items-center rounded-lg pointer hover:bg-gray-100 transition-colors group/container"
                        >
                          {/* Name & Date & Actions */}
                          <div
                            className={cn(
                              bodyStyle,
                              'w-[35%] cursor-pointer pl-3 flex items-center justify-between pr-4 group',
                            )}
                            onClick={() => handleNavigateCampaign(l)}
                          >
                            <div className="flex flex-col gap-1 overflow-hidden">
                              <div className="flex items-center gap-2">
                                {l?.display_color && (
                                  <div
                                    className="size-5 rounded-[4px]"
                                    style={{ background: l?.display_color }}
                                  />
                                )}
                                {l?.display_emoji && (
                                  <span className="chart-headers">
                                    {l?.display_emoji}
                                  </span>
                                )}
                                {l?.name?.length > 25 ? (
                                  <Tooltip title={l?.name} placement="top">
                                    <div className="new-header truncate max-w-[25ch] font-semibold text-[#202421]">
                                      {l?.name
                                        ?.trim()
                                        ?.charAt(0)
                                        .toUpperCase() +
                                        l?.name?.trim()?.slice(1).toLowerCase()}
                                    </div>
                                  </Tooltip>
                                ) : (
                                  <div className="new-header font-semibold text-[#202421]">
                                    {l?.name &&
                                      l.name?.trim()?.charAt(0).toUpperCase() +
                                        l.name?.trim()?.slice(1).toLowerCase()}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <Calendar
                                  color={'var(--BG-600)'}
                                  width={'16'}
                                  height={'16'}
                                />
                                Created @ {date?.monthInShort} {date?.date},{' '}
                                {date?.timeWithMeridiem}
                              </div>
                            </div>

                            {/* Action Buttons - Visible on Hover */}
                            <div className="w-fit flex items-center gap-2 z-50 opacity-0 group-hover/container:opacity-100 transition-opacity duration-300 ease-linear">
                              {/* Analytics Action */}
                              <Tooltip title="View Details" placement="top">
                                <button
                                  className="w-[28px] h-[28px] flex items-center justify-center rounded-md hover:bg-gray-200 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigateCampaign(l);
                                  }}
                                >
                                  <img src={ICONS?.analytics} alt="analytics" />
                                </button>
                              </Tooltip>

                              {/* Delete Action - Only for scheduled (status 10) */}
                              {l?.status === 10 && (
                                <Tooltip title="Delete" placement="top">
                                  <button
                                    className="w-[28px] h-[28px] p-[5px] rounded-md bg-transparent flex items-center justify-center hover:bg-red-100 transition-colors group/delete"
                                    onClick={(e) => handleDeleteClick(e, l)}
                                  >
                                    <Delete
                                      width="18px"
                                      height="18px"
                                      className="[&>path]:stroke-[#898e99] [&>path]:transition-colors group-hover/delete:[&>path]:!stroke-[#ef4444]"
                                    />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </div>

                          {/* Status */}
                          <div
                            className={cn(
                              bodyStyle,
                              'w-[15%] flex items-center justify-center gap-1.5',
                            )}
                            style={{ color: campaignStatus?.color }}
                            onClick={() => handleNavigateCampaign(l)}
                          >
                            <div
                              style={{
                                background: campaignStatus?.background,
                                border: `1px solid ${campaignStatus?.border}`,
                              }}
                              className="flex-row align-center rounded-full p-1 pl-10 pr-10 gap-1.5"
                            >
                              {campaignStatus?.icon && (
                                <img
                                  src={campaignStatus?.icon}
                                  alt={campaignStatus?.text}
                                />
                              )}
                              {campaignStatus?.text}
                            </div>
                          </div>

                          {/* Total Contacts */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[15%] text-center cursor-pointer',
                            )}
                            onClick={() => handleNavigateCampaign(l)}
                          >
                            {(l?.total_contacts || 0).toLocaleString()}
                          </div>

                          {/* Sent % */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[10%] text-center text-[#0F9960] cursor-pointer',
                            )}
                            onClick={() => handleNavigateCampaign(l)}
                          >
                            {l?.sent_percentage
                              ? `${l.sent_percentage.toFixed(1)}%`
                              : '0%'}
                          </div>

                          {/* Delivered % */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[10%] text-center text-[#0F9960] cursor-pointer',
                            )}
                            onClick={() => handleNavigateCampaign(l)}
                          >
                            {l?.delivered_percentage
                              ? `${l.delivered_percentage.toFixed(1)}%`
                              : '0%'}
                          </div>

                          {/* Open Rate */}
                          <div
                            className={cn(
                              'new-body-text',
                              'w-[15%] text-center text-[#6940F2] cursor-pointer',
                            )}
                            onClick={() => handleNavigateCampaign(l)}
                          >
                            {l?.open_rate ? `${l.open_rate.toFixed(1)}%` : '0%'}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {!searchKey && !status.some((s) => s.isSelected) ? (
                      <div className="w-full h-full flex justify-center mt-20">
                        <NoData
                          title="No API Campaigns yet"
                          content="API Campaigns allow you to trigger campaigns programmatically via API. Use the API to create and manage your campaigns."
                          icon={ICONS?.campaignNoData}
                        />
                      </div>
                    ) : (
                      <div className="h-[40vh] flex justify-center mt-20 pt-20">
                        <div className="flex-row h-10 mt-20">
                          <img
                            src={ICONS?.emptySearch}
                            alt="emptySearch"
                            className="w-14"
                          />
                          <div className="flex-column gap-3 pl-10">
                            <span className="new-header3">
                              Sorry, there are no matching results
                            </span>
                            <span className="no-search-body">
                              Use different filters or{' '}
                              <span
                                className="text-[#6940F2] pointer"
                                onClick={() => {
                                  setSearchKey('');
                                  clearStatus();
                                }}
                              >
                                reset to default.
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </InfiniteScroll>
            </div>
          </Skeleton>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        footer={null}
        styles={{ content: { padding: 0 } }}
        open={discardAlertOpen}
        className="asp-modal-popup-small"
        closable={false}
        centered={true}
        wrapClassName="bg-[#00000095]"
      >
        <NewAlert
          type="new"
          open={discardAlertOpen}
          icon={ICONS?.discardCarousel}
          title="Delete Campaign?"
          setOpen={setDiscardAlertOpen}
          content={
            <p>
              Are you sure you want to delete <b>"{campaignToDelete?.name}"</b>?
            </p>
          }
          noName="Yes, Delete"
          yesName="No"
          noButtonClassName="!font-md !weight-semibold"
          handleNo={() => {
            setDiscardAlertOpen(false);
            setCampaignToDelete(null);
          }}
          handleYes={() => deleteCampaign()}
          loading={deleteLoader}
        />
      </Modal>
    </div>
  );
};

export default ApiCampaigns;
